import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { createQuery } from "@tanstack/solid-query";
import { firebase } from "@core/firebase/app";
import { queryKeys } from "@core/query";
import { toDate } from "@modules/shared/firestore";
import type { RobotDetail, RobotRealtimeDetail, RobotSummary } from "./types";

const ROBOTS_COLLECTION = "robots";

const readString = (value: unknown, fallback: string | null = null): string | null => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  return fallback;
};

const readNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const readBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  return false;
};

const mapRobotSummary = (id: string, raw: Record<string, unknown>): RobotSummary => ({
  id,
  model: readString(raw["model"], id),
  displayName: readString(raw["displayName"]) ?? readString(raw["customName"]),
  status: readString(raw["status"]),
  batteryLevel: readNumber(raw["batteryLevel"]),
  locationId: readString(raw["locationId"]),
  locationHint: readString(raw["locationHint"]),
  lastSeenAt: toDate(raw["lastSeenAt"] ?? raw["updatedAt"] ?? null),
  provider: readString(raw["provider"]) ?? readString(raw["integration"] as string | undefined) ?? readString(raw["integrationProvider"]),
  manufacturer: readString(raw["manufacturer"]) ?? readString(raw["provider"]),
  providerDeviceId:
    readString(raw["providerDeviceId"]) ??
    readString(raw["externalDeviceId"]) ??
    readString(raw["deviceId"]) ??
    readString(raw["did"]) ??
    id,
  online: raw["online"] === true ? true : raw["online"] === false ? false : null,
  isActive: readBoolean(raw["isActive"]),
});

export const fetchRobots = async (): Promise<RobotSummary[]> => {
  const robotsRef = collection(firebase.firestore, ROBOTS_COLLECTION);
  const robotsQuery = query(robotsRef, orderBy("model"));
  const snapshot = await getDocs(robotsQuery);
  return snapshot.docs.map((docSnap) => mapRobotSummary(docSnap.id, docSnap.data() as Record<string, unknown>));
};

export const fetchRobotDetail = async (robotId: string): Promise<RobotDetail | null> => {
  const robotRef = doc(firebase.firestore, ROBOTS_COLLECTION, robotId);
  const snapshot = await getDoc(robotRef);
  if (!snapshot.exists()) {
    return null;
  }
  const data = snapshot.data() as Record<string, unknown>;
  const summary = mapRobotSummary(snapshot.id, data);
  const maintenance =
    typeof data["maintenance"] === "object" && data["maintenance"] !== null
      ? (data["maintenance"] as Record<string, unknown>)
      : {};
  return {
    summary,
    firmwareVersion: readString(data["firmwareVersion"]),
    maintenanceNotes: readString(maintenance["notes"]),
    maintenanceDueAt: toDate(maintenance["dueAt"]),
    assignedBookingId: readString(data["assignedBookingId"]),
    telemetry:
      typeof data["telemetry"] === "object" && data["telemetry"] !== null
        ? (data["telemetry"] as Record<string, unknown>)
        : null,
  };
};

const getRobotRealtimeCallable = httpsCallable<{ robotId: string }, { detail: RobotRealtimeDetail | null }>(
  firebase.functions,
  "getRobotRealtimeDetail",
);


export const fetchRobotRealtimeDetail = async (robotId: string): Promise<RobotRealtimeDetail | null> => {
  const response = await getRobotRealtimeCallable({ robotId });
  return response.data?.detail ?? null;
};

export const useRobotListQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.robots.root(),
    queryFn: () => fetchRobots(),
  }));

export const useRobotDetailQuery = (robotId: () => string | null) =>
  createQuery(() => {
    const id = robotId();
    return {
      queryKey: queryKeys.robots.detail(id ?? "detail"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchRobotDetail(id) : Promise.resolve(null)),
    };
  });

export const useRobotRealtimeDetailQuery = (robotId: () => string | null) =>
  createQuery(() => {
    const id = robotId();
    return {
      queryKey: queryKeys.robots.realtime(id ?? "realtime"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchRobotRealtimeDetail(id) : Promise.resolve(null)),
    };
  });

